<template>
  <main class="digital-card-page">
    <div class="phone-shell">
      <header class="mobile-header">
        <button class="header-action" type="button" aria-label="Retour accueil" @click="goHome">
          <span aria-hidden="true">‹</span>
        </button>

        <div class="brand-lockup">
          <div class="logo-mark">FID</div>
          <div>
            <p>FIDmarket</p>
            <span>Carte fidélité</span>
          </div>
        </div>

        <span class="active-badge">
          <span></span>
          Actif
        </span>
      </header>

      <section class="loyalty-card" aria-label="Carte fidélité digitale">
        <div class="card-shine"></div>
        <div class="card-topline">
          <div>
            <p class="card-brand">FIDmarket</p>
            <h1>{{ mockCommerce.name }}</h1>
          </div>
          <span>{{ mockCommerce.badge }}</span>
        </div>

        <div class="customer-row">
          <div>
            <p>Client</p>
            <strong>{{ mockClient.name }}</strong>
          </div>
          <div>
            <p>Carte</p>
            <strong>{{ mockClient.cardNumber }}</strong>
          </div>
        </div>

        <div class="points-panel">
          <div>
            <p>Solde actuel</p>
            <strong>{{ mockClient.points }} points</strong>
          </div>
          <span>{{ pointsToNextReward }} pts restants</span>
        </div>

        <div class="progress-block">
          <div class="progress-track">
            <div class="progress-fill" :style="{ width: progressWidth }"></div>
          </div>
          <p>{{ pointsToNextReward }} points avant votre prochain bon</p>
          <div class="progress-dots" aria-hidden="true">
            <span class="done"></span>
            <span class="done"></span>
            <span class="done"></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </section>

      <section class="qr-card section-card">
        <div class="section-heading">
          <div>
            <span class="kicker">Identification</span>
            <h2>Votre carte digitale</h2>
          </div>
          <span class="mini-icon" aria-hidden="true"></span>
        </div>

        <div class="qr-preview" :class="{ expanded: qrVisible }">
          <div class="fake-qr" aria-hidden="true">
            <span v-for="index in 49" :key="index" :class="{ dark: qrPattern.includes(index) }"></span>
          </div>
          <p>Scannez pour afficher votre carte</p>
        </div>

        <button class="primary-button" type="button" @click="qrVisible = !qrVisible">
          {{ qrVisible ? 'Masquer mon QR Code' : 'Afficher mon QR Code' }}
        </button>
      </section>

      <section class="section-card how-it-works">
        <div class="section-heading">
          <div>
            <span class="kicker">Fonctionnement</span>
            <h2>Simple à utiliser au marché</h2>
          </div>
        </div>

        <ul>
          <li v-for="rule in rules" :key="rule.title">
            <span class="rule-icon" aria-hidden="true">{{ rule.icon }}</span>
            <div>
              <strong>{{ rule.title }}</strong>
              <p>{{ rule.text }}</p>
            </div>
          </li>
        </ul>
      </section>

      <section class="status-grid" aria-label="Statut fidélité">
        <article v-for="stat in stats" :key="stat.label" class="stat-card">
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </article>
      </section>

      <section class="reward-card section-card">
        <div class="reward-copy">
          <span class="available-badge">Disponible</span>
          <h2>Bon d’achat 5€</h2>
          <p>Disponible dès 100 points</p>
        </div>
        <button class="secondary-button" type="button">Utiliser plus tard</button>
      </section>

      <section class="section-card history-card">
        <div class="section-heading">
          <div>
            <span class="kicker">Historique</span>
            <h2>Derniers mouvements</h2>
          </div>
        </div>

        <div class="history-list">
          <article v-for="item in mockHistory" :key="item.label" class="history-row">
            <div>
              <strong>{{ item.label }}</strong>
              <span>{{ item.date }}</span>
            </div>
            <span :class="{ negative: item.points < 0 }">
              {{ item.points > 0 ? '+' : '' }}{{ item.points }} pts
            </span>
          </article>
        </div>
      </section>

      <footer class="digital-footer">
        <strong>FIDmarket — La fidélité qui rapproche</strong>
        <span>Carte physique + digitale</span>
      </footer>
    </div>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'

const mockClient = {
  name: 'Marie L.',
  cardNumber: 'FID-HER-000001',
  points: 125,
  nextReward: 200,
  rewardsAvailable: 1
}

const mockCommerce = {
  name: 'Marché Herrera',
  badge: 'BIO & LOCAL'
}

const mockHistory = [
  { label: 'Achat chez Marché Herrera', date: 'Aujourd’hui', points: 25 },
  { label: 'Achat produits bio', date: 'Samedi dernier', points: 40 },
  { label: 'Récompense utilisée', date: '12 mai', points: -100 }
]

const qrVisible = ref(false)
const animatedProgress = ref(0)
const rewardStep = 100

const pointsToNextReward = computed(() => mockClient.nextReward - mockClient.points)
const progressTarget = computed(() => Math.min((mockClient.points / mockClient.nextReward) * 100, 100))
const progressWidth = computed(() => `${animatedProgress.value}%`)

const rules = [
  { icon: '€', title: '1€ d’achat = 1 point', text: 'Les achats du quotidien font progresser la cagnotte fidélité.' },
  { icon: '✓', title: '100 points = 1 bon d’achat', text: 'Les récompenses sont visibles dès qu’elles deviennent disponibles.' },
  { icon: 'ID', title: 'Carte retrouvable facilement', text: 'Nom, prénom ou téléphone suffisent pour retrouver le client.' },
  { icon: '↺', title: 'Points toujours conservés', text: 'Même sans carte physique, l’historique fidélité reste disponible.' }
]

const stats = computed(() => [
  { label: 'Points cumulés', value: mockClient.points },
  { label: 'Prochain palier', value: mockClient.nextReward },
  { label: 'Récompenses', value: mockClient.rewardsAvailable }
])

const qrPattern = [
  1, 2, 3, 5, 6, 7, 8, 10, 12, 13, 15, 17, 19, 21, 22, 24, 26, 28, 29, 30, 32, 34, 36, 38, 40, 41, 43, 45, 46, 47, 49
]

const goHome = () => {
  window.history.length > 1 ? window.history.back() : null
}

onMounted(() => {
  window.requestAnimationFrame(() => {
    animatedProgress.value = progressTarget.value
  })
})
</script>

<style scoped>
.digital-card-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(30, 111, 220, 0.11), transparent 34rem),
    linear-gradient(180deg, #f8fafc 0%, #edf3fb 100%);
  color: #0f172a;
}

.phone-shell {
  width: 100%;
  max-width: 430px;
  min-height: 100vh;
  margin: 0 auto;
  background: #f8fafc;
  overflow-x: hidden;
  box-shadow: 0 30px 80px rgba(13, 27, 61, 0.12);
}

.mobile-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 18px 24px;
  background:
    linear-gradient(135deg, rgba(30, 111, 220, 0.28), transparent 46%),
    linear-gradient(180deg, #0d1b3d 0%, #132857 100%);
  color: #ffffff;
}

.header-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
}

.header-action span {
  display: block;
  margin-top: -2px;
  font-size: 30px;
  line-height: 1;
}

.brand-lockup {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.logo-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 14px;
  background: #ffffff;
  color: #0d1b3d;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.brand-lockup p,
.brand-lockup span {
  margin: 0;
  color: #ffffff;
}

.brand-lockup p {
  font-size: 15px;
  font-weight: 800;
}

.brand-lockup span {
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  font-weight: 600;
}

.active-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  padding: 8px 10px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.14);
  color: #dcfce7;
  border: 1px solid rgba(134, 239, 172, 0.32);
  font-size: 12px;
  font-weight: 800;
}

.active-badge span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.14);
}

.loyalty-card {
  position: relative;
  margin: -8px 18px 16px;
  padding: 22px;
  overflow: hidden;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.18), transparent 36%),
    linear-gradient(145deg, #0d1b3d 0%, #112b64 54%, #1e6fdc 130%);
  color: #ffffff;
  box-shadow: 0 22px 50px rgba(13, 27, 61, 0.28);
}

.card-shine {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 88% 12%, rgba(255, 255, 255, 0.22), transparent 7rem),
    linear-gradient(115deg, transparent 0%, rgba(255, 255, 255, 0.1) 48%, transparent 58%);
}

.card-topline,
.customer-row,
.points-panel,
.progress-block {
  position: relative;
  z-index: 1;
}

.card-topline {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 34px;
}

.card-brand {
  margin: 0 0 8px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.card-topline h1 {
  margin: 0;
  color: #ffffff;
  font-size: 23px;
  font-weight: 800;
}

.card-topline span,
.points-panel span {
  flex: 0 0 auto;
  padding: 7px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #e8f1fd;
  font-size: 11px;
  font-weight: 800;
}

.customer-row,
.points-panel {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  align-items: end;
}

.customer-row {
  margin-bottom: 18px;
}

.customer-row p,
.points-panel p {
  margin: 0 0 4px;
  color: rgba(255, 255, 255, 0.64);
  font-size: 11px;
  font-weight: 700;
}

.customer-row strong {
  color: #ffffff;
  font-size: 15px;
  font-weight: 800;
}

.points-panel strong {
  color: #ffffff;
  font-size: 29px;
  font-weight: 800;
}

.progress-block {
  margin-top: 18px;
}

.progress-track {
  height: 9px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #22c55e 0%, #86efac 100%);
  transition: width 0.9s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.progress-block p {
  margin: 10px 0 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: 12px;
  font-weight: 600;
}

.progress-dots {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}

.progress-dots span {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.24);
}

.progress-dots .done {
  background: #22c55e;
}

.section-card,
.stat-card {
  margin: 0 18px 14px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
}

.section-card {
  padding: 18px;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.kicker {
  display: block;
  margin-bottom: 4px;
  color: #1e6fdc;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.section-heading h2,
.reward-copy h2 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
}

.mini-icon {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  background:
    linear-gradient(90deg, #0d1b3d 30%, transparent 30% 40%, #0d1b3d 40% 70%, transparent 70%),
    linear-gradient(#1e6fdc 0 0) center / 18px 18px no-repeat,
    #e8f1fd;
}

.qr-preview {
  display: grid;
  place-items: center;
  gap: 12px;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.28s ease, opacity 0.22s ease, margin 0.22s ease;
}

.qr-preview.expanded {
  max-height: 220px;
  margin-bottom: 16px;
  opacity: 1;
}

.fake-qr {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
  width: 148px;
  height: 148px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: inset 0 0 0 7px #f8fafc;
}

.fake-qr span {
  border-radius: 4px;
  background: #edf2f7;
}

.fake-qr .dark {
  background: #0d1b3d;
}

.qr-preview p,
.reward-copy p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.primary-button,
.secondary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.primary-button {
  background: linear-gradient(180deg, #247bea 0%, #1e6fdc 100%);
  color: #ffffff;
  box-shadow: 0 12px 24px rgba(30, 111, 220, 0.2);
}

.secondary-button {
  background: #f8fafc;
  color: #0d1b3d;
  border: 1px solid #e2e8f0;
}

.how-it-works ul {
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.how-it-works li {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.rule-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 13px;
  background: #e8f1fd;
  color: #1e6fdc;
  font-size: 12px;
  font-weight: 900;
}

.how-it-works strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  font-weight: 800;
}

.how-it-works p {
  margin: 2px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 0 18px 14px;
}

.stat-card {
  min-width: 0;
  margin: 0;
  padding: 14px 10px;
}

.stat-card span {
  display: block;
  min-height: 32px;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.25;
}

.stat-card strong {
  display: block;
  margin-top: 8px;
  color: #0d1b3d;
  font-size: 23px;
  font-weight: 900;
}

.reward-card {
  display: grid;
  gap: 16px;
  background:
    linear-gradient(135deg, rgba(34, 197, 94, 0.12), transparent 40%),
    #ffffff;
}

.available-badge {
  display: inline-flex;
  width: fit-content;
  margin-bottom: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
  font-size: 12px;
  font-weight: 900;
}

.history-list {
  display: grid;
  gap: 4px;
}

.history-row {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eef2f7;
}

.history-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.history-row strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  font-weight: 800;
}

.history-row div span {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.history-row > span {
  flex: 0 0 auto;
  color: #16a34a;
  font-size: 13px;
  font-weight: 900;
}

.history-row > span.negative {
  color: #ef4444;
}

.digital-footer {
  display: grid;
  place-items: center;
  gap: 4px;
  padding: 10px 18px 28px;
  text-align: center;
}

.digital-footer strong {
  color: #0f172a;
  font-size: 12px;
  font-weight: 800;
}

.digital-footer span {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

@media (hover: hover) {
  .header-action:hover,
  .primary-button:hover,
  .secondary-button:hover {
    transform: translateY(-1px);
  }

  .primary-button:hover {
    background: linear-gradient(180deg, #1e6fdc 0%, #155fc4 100%);
    box-shadow: 0 14px 28px rgba(30, 111, 220, 0.24);
  }

  .secondary-button:hover {
    background: #f1f5f9;
  }
}

@media (max-width: 359px) {
  .mobile-header,
  .loyalty-card,
  .section-card,
  .status-grid {
    margin-left: 12px;
    margin-right: 12px;
  }

  .mobile-header {
    padding-left: 12px;
    padding-right: 12px;
  }

  .status-grid {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 640px) {
  .digital-card-page {
    padding: 32px 0;
  }

  .phone-shell {
    min-height: auto;
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 32px;
    overflow: hidden;
  }
}
</style>
